"use client";

import { useSyncExternalStore } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { MainCard, MainCardContent, MainCardHeader, MainCardTitle } from "@/components/ui/main-card";
import { RankingTable } from "@/components/ranking-table";
import SearchTimelineSkeleton from "@/components/search/SearchTimelineSkeleton";
import MarketAnalysisSkeleton from "@/components/search/MarketAnalysisSkeleton";
import MarketConcentrationSkeleton from "@/components/search/MarketConcentrationSkeleton";
import CompetitiveIntensitySkeleton from "@/components/search/CompetitiveIntensitySkeleton";
import AppAgeDistributionSkeleton from "@/components/search/AppAgeDistributionSkeleton";
import NewEntrantsSkeleton from "@/components/search/NewEntrantsSkeleton";
import CompetitiveBubbleSkeleton from "@/components/search/CompetitiveBubbleSkeleton";
import { useThemeConfig } from "@/hooks/use-theme-config";
import { cn } from "@/lib/utils";

export default function AppDetailProSkeleton() {
  const { isTerminal, hasGlow } = useThemeConfig();

  // Hydration-safe pattern: only show terminal styling after mount
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );

  const showTerminal = mounted && isTerminal;
  const showGlow = mounted && hasGlow;

  return (
    <div id="app-detail-pro-skeleton" className={cn(
      "text-foreground mx-auto",
      showTerminal && "bg-black"
    )}>
      <div className="grid grid-cols-1 lg:grid-cols-3 lg:h-[calc(100vh-3rem)] lg:overflow-hidden">
        {/* Column 1: Main App Details */}
        <div className="md:p-4 p-4 lg:h-[calc(100vh-3rem)] lg:overflow-y-auto pb-24 col-span-1 space-y-4">
          {/* Header */}
          <div className={cn(
            "flex items-center gap-4",
            showTerminal && "p-3 border border-emerald-500/20 rounded-none",
            showGlow && "shadow-[0_0_8px_rgba(16,185,129,0.1)]"
          )}>
            <Skeleton className={cn(
              "h-20 w-20 rounded-2xl shadow-sm",
              !showTerminal && "ring-1 ring-inset ring-black/[0.02] dark:ring-white/[0.02]"
            )} />
            <div className="flex-1 min-w-0">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-3 w-1/3 mt-2" />
            </div>
            <Skeleton className="h-7 w-7 rounded-md" />
          </div>

          {/* All 6 cards in single grid */}
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <MainCard
                key={i}
                className={cn(
                  "p-2 h-24 md:h-28 flex flex-col relative overflow-hidden",
                  showTerminal
                    ? "border border-emerald-500/20 rounded-none bg-black"
                    : "border-0 shadow-sm ring-1 ring-inset ring-black/5 dark:ring-white/5"
                )}
              >
                {/* Terminal mode corner accents */}
                {showTerminal && (
                  <>
                    <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-emerald-500/50" />
                    <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-emerald-500/50" />
                    <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-emerald-500/50" />
                    <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-emerald-500/50" />
                  </>
                )}

                {/* Texture & Gradient Overlay - only for non-terminal mode */}
                {!showTerminal && (
                  <>
                    <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.05'/%3E%3C/svg%3E")` }} />
                    <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-white/80 to-transparent dark:from-white/[0.03] dark:to-transparent" />
                  </>
                )}

                {/* Terminal mode gradient */}
                {showTerminal && (
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-emerald-500/5 to-transparent" />
                )}

                <div className="relative z-10 flex flex-col h-full">
                  <div className="flex items-start justify-between">
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="flex-1" />
                  <Skeleton className="h-8 w-24" />
                </div>
              </MainCard>
            ))}
          </div>

          {/* Chart card */}
          <MainCard className={cn(
            showTerminal && "border border-emerald-500/20 rounded-none bg-black"
          )}>
            <MainCardHeader>
              <MainCardTitle className={cn(
                showTerminal ? "text-emerald-500/80 font-mono text-xs uppercase tracking-widest" : "text-muted-foreground"
              )}>
                {showTerminal ? "RANKING.HISTORY" : "Ranking History"}
              </MainCardTitle>
            </MainCardHeader>
            <MainCardContent>
              <Skeleton className="h-56 w-full" showLoadingText />
            </MainCardContent>
          </MainCard>
        </div>

        {/* Column 2: Competitors (top) + Country Rankings (bottom) */}
        <div className={cn(
          "lg:h-[calc(100vh-3rem)] col-span-1 flex flex-col",
          showTerminal ? "border-r border-emerald-500/20" : "border-r border-sidebar-border"
        )}>
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
            <div className={cn(
              "p-3 border-b flex-shrink-0 flex items-center justify-between h-[53px] box-border",
              showTerminal ? "border-emerald-500/20 bg-black/50" : ""
            )}>
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-8 w-8 rounded-md" />
            </div>
            <div className="flex-1 overflow-hidden">
              <RankingTable
                data={[]}
                isLoading={true}
                showChangeColumn={true}
                reviewsHeaderStacked
                nameColClassName="w-28 sm:w-36 md:w-44"
                showExternalLink={false}
              />
            </div>
          </div>
          <div className={cn(
            "flex-1 min-h-0 overflow-y-auto flex flex-col border-t",
            showTerminal ? "border-emerald-500/20" : "border-sidebar-border"
          )}>
            <div className={cn(
              "p-3 border-b flex-shrink-0 flex items-center justify-between h-[53px] box-border",
              showTerminal ? "border-emerald-500/20 bg-black/50" : ""
            )}>
              <Skeleton className="h-4 w-32" />
            </div>
            <div className={cn(
              "flex-1 p-2 flex items-center justify-center overflow-hidden",
              showTerminal ? "bg-black" : "bg-background"
            )}>
              {/* Globe Skeleton: 3D effect - terminal mode uses emerald */}
              <div className={cn(
                "relative w-[360px] h-[360px] rounded-full animate-pulse",
                showTerminal
                  ? "bg-[radial-gradient(circle_at_30%_30%,_#064e3b,_#000)] shadow-[inset_-10px_-10px_20px_rgba(0,0,0,0.5),_0_0_20px_rgba(16,185,129,0.15)]"
                  : "bg-[radial-gradient(circle_at_30%_30%,_#F3F4F6,_#D1D5DB)] dark:bg-[radial-gradient(circle_at_30%_30%,_#2A2A2A,_#000)] shadow-[inset_-10px_-10px_20px_rgba(0,0,0,0.1),_0_0_20px_rgba(249,115,22,0.15)] dark:shadow-[inset_-10px_-10px_20px_rgba(0,0,0,0.5),_0_0_20px_rgba(0,210,106,0.1)]"
              )}>
                <div className={cn(
                  "absolute inset-0 rounded-full",
                  showTerminal
                    ? "shadow-[0_0_30px_rgba(16,185,129,0.2)]"
                    : "shadow-[0_0_30px_rgba(249,115,22,0.2)] dark:shadow-[0_0_30px_rgba(0,210,106,0.15)]"
                )} />
              </div>
            </div>
          </div>
        </div>

        {/* Column 3: Market Intelligence & Competitive Analysis */}
        <div className="lg:h-[calc(100vh-3rem)] lg:overflow-y-auto pb-24 col-span-1 space-y-4 p-4">
          <SearchTimelineSkeleton rounded={!showTerminal} />
          <MarketAnalysisSkeleton />
          <MarketConcentrationSkeleton />
          <CompetitiveIntensitySkeleton />
          <AppAgeDistributionSkeleton />
          <NewEntrantsSkeleton />
          <CompetitiveBubbleSkeleton />
        </div>
      </div>
    </div>
  )
}
